import { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Checkbox, Modal, Select, type SelectItem } from "@synthesis/shared-ui";

import { AdminModalBody, AdminModalFooter } from "../../../components/admin-console/admin-modal";
import { useRailways } from "../../../shared/hooks/use-railways";
import { DOCUMENT_KIT_DOCUMENTS, usePageStore } from "../constructor-documents-page.lib";

const SERVICE_OPTIONS: SelectItem[] = [
  { value: "TU-ISSUE", label: "Выдача ТУ" },
  { value: "PPR", label: "ППР" },
  { value: "DOC-APPROVE", label: "Согласование документации" },
  { value: "AKT-DOPUSK", label: "Акт-допуска" },
];
const CONNECTION_OPTIONS: SelectItem[] = [
  { value: "any", label: "Любой" },
  { value: "adjacent", label: "Примыкание" },
  { value: "crossing", label: "Пересечение" },
];
const APPLICANT_OPTIONS: SelectItem[] = [
  { value: "any", label: "Любой" },
  { value: "legal-entity", label: "Юридическое лицо" },
  { value: "individual", label: "Индивидуальный предприниматель" },
];

/** Модалка «Новый комплект документов» (макет «Документы - Добавление документов»). Открывается через стор. Бэка нет — сохранение только закрывает окно. */
export const NewDocumentKitModal = observer(() => {
  const store = usePageStore()!;
  const isOpen = store.popupAction?.type === "add-document-kit";
  const { railways } = useRailways();
  const [service, setService] = useState("");
  const [road, setRoad] = useState("");
  const [connection, setConnection] = useState("");
  const [applicant, setApplicant] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  // Дороги из базового справочника (как в заявке); «Любая» — первая опция.
  const roadOptions = useMemo<SelectItem[]>(
    () => [{ value: "any", label: "Любая" }, ...railways.map((railway) => ({ value: railway.value, label: railway.label }))],
    [railways],
  );

  const handleClose = useCallback(() => {
    setService("");
    setRoad("");
    setConnection("");
    setApplicant("");
    setSelectedDocuments([]);
    store.setPopupAction(null);
  }, [store]);

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={(open) => (open ? null : handleClose())}>
      <Modal.Content className="w-[43.5rem] max-w-[90vw]">
        <Modal.Title>Новый комплект документов</Modal.Title>
        <AdminModalBody>
          <h4 className="heading-h4 text-type-common">Новый комплект документов</h4>

          <div className="grid grid-cols-2 gap-4">
            <Select label="Услуга *" placeholder="Выберите из списка" items={SERVICE_OPTIONS} value={service} onChange={setService} />
            <Select label="Железная дорога" placeholder="Выберите из списка" items={roadOptions} value={road} onChange={setRoad} />
            <Select
              label="Тип присоединения"
              placeholder="Выберите из списка"
              items={CONNECTION_OPTIONS}
              value={connection}
              onChange={setConnection}
            />
            <Select label="Вид заявителя" placeholder="Выберите из списка" items={APPLICANT_OPTIONS} value={applicant} onChange={setApplicant} />
          </div>

          <div className="flex flex-col gap-2">
            <p className="paragraph-sm text-type-common">Документы комплекта</p>
            <div className="border-neutrals-90 flex max-h-64 flex-col overflow-y-auto border-b">
              {DOCUMENT_KIT_DOCUMENTS.map((document) => (
                <div key={document.id} className="border-neutrals-90 flex items-center justify-between border-y py-2">
                  <Checkbox
                    checked={selectedDocuments.includes(document.id)}
                    onChange={(checked) =>
                      setSelectedDocuments((current) => (checked ? [...current, document.id] : current.filter((id) => id !== document.id)))
                    }
                    label={document.name}
                  />
                  <span className={document.required ? "paragraph-sm text-type-minor" : "paragraph-sm text-type-light"}>
                    {document.required ? "Обязателен" : "По желанию"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </AdminModalBody>

        <AdminModalFooter>
          {/* TODO(FRONT-1390): добавление комплекта — после появления бэкенда. */}
          <Button theme="primary" size="lg" onClick={handleClose}>
            Добавить комплект
          </Button>
          <Button theme="secondary" size="lg" onClick={handleClose}>
            Отмена
          </Button>
        </AdminModalFooter>
      </Modal.Content>
    </Modal.Root>
  );
});
